import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import { map } from 'lodash-es';
import { List, SearchBox, Segment, StatusTip } from 'tea-component';
import { useIntl } from 'umi';
import { LANGUAGE_EN } from '@home/_config/config';
import BasicImage from '@home/components/BasicImage';
import HighlightText from '@/pages/aigc/components/HighlightText';
import AgentCollectionDefaultIcon from '@/pages/aigc/assets/svg/agent-collection-default-icon.svg';
import SkillsCollectionDefaultIcon from '@/pages/aigc/assets/svg/skills-collection-default-icon.svg';
import SkillsCollectionIcon from '@/pages/aigc/assets/svg/icon-skills-collection.svg';
import SkillsDefaultIcon from '@/pages/aigc/assets/svg/skills-default-icon.svg';
import { useTabTemplate } from '../../../Home/hooks/useTabTemplate';
import { useSkillsData } from '../../../SkillsCollection/hooks';
import { getSkillDisplayName } from '../../../SkillsCollection/utils/display';
import AgentCollectionIcon from '../../../svg/agent-mdel-collection-icon-menu.svg';
import AgentModeDefaultIcon from '../../../svg/agent-mode-default-icon.svg';
import { FILE_LIBRARY_SOURCE_TABS } from '../../constants';
import type { IAgentData } from '../../../AgentCollection/types';
import type {
  TFileLibrarySource,
  TFileLibrarySourceItem,
  TFileLibrarySourceTab,
} from '../../types';
import { filterSourceItems } from '../../utils';

const getSourceTabIcon = (
  tabId: TFileLibrarySourceTab,
  isSelected: boolean,
): string => {
  if (tabId === 'skill') {
    return isSelected ? SkillsCollectionIcon : SkillsCollectionDefaultIcon;
  }
  return isSelected ? AgentCollectionIcon : AgentCollectionDefaultIcon;
};

/** 来源筛选下拉 */
const SourceSelect: React.FC<{
  value: TFileLibrarySource;
  agents: IAgentData[];
  agentsLoading?: boolean;
  onChange: (value: TFileLibrarySource) => void;
}> = ({ value, agents, agentsLoading, onChange }) => {
  const { locale, formatMessage } = useIntl();
  const isEN = locale === LANGUAGE_EN;
  const [keyword, setKeyword] = useState('');
  const { allSkills, loading: skillsLoading } = useSkillsData(isEN);

  const sourceTabs = useMemo(
    () =>
      FILE_LIBRARY_SOURCE_TABS.map((tab) => ({
        id: tab.id,
        label: formatMessage({ id: tab.labelId }),
      })),
    [formatMessage],
  );

  const { activeId, onTabChange } = useTabTemplate({
    tabs: sourceTabs,
    defaultActiveId: 'agent',
    onTabChangeCallback: () => setKeyword(''),
  });
  const sourceTabId: TFileLibrarySourceTab =
    activeId === 'skill' ? 'skill' : 'agent';

  const allItems: TFileLibrarySourceItem[] = useMemo(() => {
    if (sourceTabId === 'agent') {
      return map(agents, (agent) => ({
        id: agent.agentId,
        name: isEN ? agent.name : agent.name_cn || agent.name,
        icon: agent.icon,
      }));
    }
    return map(allSkills, (skill) => ({
      id: skill.skill_id,
      name: getSkillDisplayName(skill, isEN),
      icon: skill.category_icon || SkillsDefaultIcon,
      skillName: skill.skill_name,
    }));
  }, [agents, allSkills, isEN, sourceTabId]);

  const filteredItems = useMemo(
    () => filterSourceItems(allItems, keyword),
    [allItems, keyword],
  );
  const fallbackIcon =
    sourceTabId === 'skill' ? SkillsDefaultIcon : AgentModeDefaultIcon;
  const listLoading =
    sourceTabId === 'skill' ? skillsLoading : Boolean(agentsLoading);
  const searchKeyword = keyword.trim();

  const segmentOptions = sourceTabs.map((tab) => {
    const isSelected = sourceTabId === tab.id;
    return {
      value: tab.id,
      text: (
        <span className="dba-file-library-source__tab-label">
          <img src={getSourceTabIcon(tab.id, isSelected)} alt="" />
          {tab.label}
        </span>
      ),
    };
  });

  return (
    <div className="dba-file-library-source">
      <SearchBox
        placeholder={formatMessage({
          id: 'dba_file_library_source_search',
        })}
        value={keyword}
        onChange={setKeyword}
        onClear={() => setKeyword('')}
      />
      <Segment
        className="dba-file-library-source__segment"
        value={sourceTabId}
        options={segmentOptions}
        onChange={onTabChange}
      />
      <div
        className={classNames('dba-file-library-source__list', {
          'dba-file-library-source__list--loading': listLoading,
        })}
      >
        {listLoading ? (
          <StatusTip
            status="loading"
            loadingText={formatMessage({ id: 'dba_file_library_loading' })}
          />
        ) : (
          <List type="option">
            {map(filteredItems, (item) => {
              const isActive =
                value?.kind === sourceTabId && value?.id === item.id;
              return (
                <List.Item
                  key={item.id}
                  className={classNames('dba-file-library-source__item', {
                    'is-selected': isActive,
                    'dba-file-library-source__item--active': isActive,
                    'dba-file-library-source__item--skill':
                      sourceTabId === 'skill',
                  })}
                  onClick={() =>
                    onChange({
                      kind: sourceTabId,
                      id: item.id,
                      name: item.name,
                      icon: item.icon,
                      skillName: item.skillName,
                    })
                  }
                >
                  <BasicImage
                    src={item.icon || fallbackIcon}
                    errorSrc={fallbackIcon}
                  />
                  <HighlightText
                    name={item.name}
                    keyword={searchKeyword}
                    ellipsis
                  />
                </List.Item>
              );
            })}
          </List>
        )}
      </div>
    </div>
  );
};

export default SourceSelect;
